/**
 * Copyright 2026 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { useState, useMemo } from 'react';
import { LittlePromoContent } from './LittlePromoContent';
import { useAddToCartWithMiniCart } from '@/features/certona/hooks/useAddToCartWithMiniCart';
import { useHeaderData } from '@/features/header/api/getHeader';
import { useStoreDetailsData } from '@/features/header/api/getStoreDetails';
import { useGetProductSkuPrice } from '@/features/product/api/getProductSkuPrice';
import type { SkuAvailabilty } from '@/types/availability';
import { useReviewStatistics } from '@/features/customReviews/api/getReviewStatistics';
import { FULFILLMENT_METHODS } from '@/constants/fulfillmentConstants';
import { defaultStoreUS } from '@/constants/locale';
import type { AddToCartRequestType } from '@/features/certona';
import type { CustomLinkName } from '@/types/analytics';
import { formatPrice } from '@/utils/validator/formatPrice';
import type { AddToCartData } from '@/types';
import type { RefObject } from 'react';
import { useLabels } from '@/hooks/useLabels';
import { LITTLE_PROMO_CONFIG } from '../config/littlePromoConfig';
import { buildUrlWithAcesParams } from '@/utils/buildUrlWithAcesParams';

const labelMap = {
  productName: 'label_miniCart_littlePromo_productName',
};

type LittlePromoProps = {
  updateMiniCartItem?: (item?: AddToCartData & { orderType?: string }) => void;
  parentRef?: RefObject<HTMLDivElement | null>;
  productAvailability?: SkuAvailabilty[];
};

export const LittlePromo = ({
  updateMiniCartItem,
  parentRef,
  productAvailability,
}: LittlePromoProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isAdded, setIsAdded] = useState(false);

  const labels = useLabels(labelMap);
  const { addToCartCall, addingToCartStatus } = useAddToCartWithMiniCart(true);
  const { data: headerData } = useHeaderData();
  const { data: storeDetailsData } = useStoreDetailsData();

  const productDetails = productAvailability?.[0]?.attributesInfoVO;

  const storeNumber = headerData?.storeNumber ?? defaultStoreUS;
  const { data: pricingInfo } = useGetProductSkuPrice({
    skuNumbers: [LITTLE_PROMO_CONFIG.SKU_ID],
    storeNumber,
    enabled: !!storeNumber,
    staleTime: 5 * 60000,
  });

  const { data: reviewStatistics } = useReviewStatistics({
    skuNumbers: [LITTLE_PROMO_CONFIG.SKU_ID],
    enabled: true,
  });

  const productPrice = pricingInfo?.[LITTLE_PROMO_CONFIG.SKU_ID];
  const displayPrice = productPrice?.retailPrice
    ? `$${formatPrice(productPrice.retailPrice)}`
    : LITTLE_PROMO_CONFIG.PROMOTION_PRICE;

  const reviewData = reviewStatistics?.[LITTLE_PROMO_CONFIG.SKU_ID];
  const averageRating = reviewData?.averageOverallRating
    ? Number(reviewData.averageOverallRating)
    : 0;
  const totalReviews = reviewData?.totalReviewCount ?? '0';
  const hasReviews = averageRating > 0 && Number(totalReviews) > 0;

  const productImageUrl = productDetails?.productImage ?? '';
  const productSeoUrl = buildUrlWithAcesParams(productDetails?.productURL ?? '', {
    partGroupId: LITTLE_PROMO_CONFIG.PRODUCT_ID,
    brandId: LITTLE_PROMO_CONFIG.BRAND_ID,
    positionId: undefined,
    uniqueId: LITTLE_PROMO_CONFIG.UNIQUE_ID,
  });
  const productName = labels.productName;
  const productBrand = productDetails?.brandName ?? '';
  const productPartNumber = productDetails?.productPartNumber ?? '';

  const fulfillmentId = useMemo(() => {
    if (!productAvailability || productAvailability.length === 0) {
      return FULFILLMENT_METHODS.ONLINEORDER;
    }

    const sthQuantity = productAvailability[0]?.availabilityInfoVO?.sthQuantity ?? 0;

    if (sthQuantity > 0) {
      return FULFILLMENT_METHODS.ONLINEORDER;
    }
    return FULFILLMENT_METHODS.ONLINEORDER;
  }, [productAvailability]);

  const handleAddToCart = () => {
    if (isAdded || addingToCartStatus === 'pending') {
      return;
    }

    setIsLoading(true);

    const catalogVehicleId = headerData?.vehicleMap?.vehicleId ?? '';
    const storeNumber = String(storeDetailsData?.storeNumber ?? '');

    const addToCartRequest: AddToCartRequestType = {
      sku_id: LITTLE_PROMO_CONFIG.SKU_ID,
      product_id: LITTLE_PROMO_CONFIG.PRODUCT_ID,
      quantity: '1',
      category_id: '',
      order_type: fulfillmentId,
      catalog_vehicle_id: catalogVehicleId,
      store_number: storeNumber,
      orderType: String(fulfillmentId),
      requestFromCertona: false,
      uniqueId: String(LITTLE_PROMO_CONFIG.UNIQUE_ID),
    };

    const { pageName = '', pageType = '' } = window.utag_data ?? {};
    const promotionPrice = Number(LITTLE_PROMO_CONFIG.PROMOTION_PRICE.replace('$', ''));

    const discountAmount = productPrice?.retailPrice
      ? (productPrice.retailPrice - promotionPrice).toFixed(2)
      : '0.00';

    const analyticsData = {
      price: displayPrice.replace('$', ''),
      pageName: pageName,
      customLinkName: 'Mini_Cart-Promo:Add_to_Cart' as CustomLinkName,
      pageType: pageType,
      productBrand,
      originalPartTypeId: null,
      productRepositoryId: LITTLE_PROMO_CONFIG.PRODUCT_ID,
      productPartNumber,
      productRecommendationTypeList: 'Promo',
      productFindingMethod: 'Product Recommendation',
      productRecommendationInteraction: 'productRecommendationInteraction',
      productRecommendationType: 'Mini Cart: Special Offer',
      productStrikeThroughPricing: ['0'],
      fulfillmentChange: '',
      corePrice: productPrice?.corePrice,
      productFulfillmentOptionSeenByCustomer: Number(fulfillmentId),
      productDealIdsClaimIdsList: LITTLE_PROMO_CONFIG.productDealIdsClaimIdsList,
      productDiscountPriceList: discountAmount,
      productlevelsubTotalAmount: LITTLE_PROMO_CONFIG.productlevelsubTotalAmount,
    };

    const onSuccess = () => {
      setIsAdded(true);
      setIsLoading(false);

      if (updateMiniCartItem) {
        const addToCartAnalyticsData = {
          price: displayPrice.replace('$', ''),
          imageUrl: productImageUrl,
          label: productName,
          skuId: LITTLE_PROMO_CONFIG.SKU_ID,
          productId: LITTLE_PROMO_CONFIG.PRODUCT_ID,
          fulFillmentId: fulfillmentId,
          brandName: productBrand,
          productPartNumber,
          productRepositoryId: LITTLE_PROMO_CONFIG.PRODUCT_ID,
          orderType:
            fulfillmentId === FULFILLMENT_METHODS.ONLINEORDER ? 'ONLINEORDER' : 'STOREORDER',
        };

        updateMiniCartItem(addToCartAnalyticsData);

        setTimeout(() => {
          parentRef?.current?.scrollTo(0, 0);
        }, 500);
      }
    };

    const onSettled = () => {
      setIsLoading(false);
    };

    addToCartCall(addToCartRequest, analyticsData, catalogVehicleId, onSuccess, onSettled);
  };

  if (!productDetails) {
    return null;
  }

  return (
    <LittlePromoContent
      displayPrice={displayPrice}
      promotionPrice={LITTLE_PROMO_CONFIG.PROMOTION_PRICE}
      productImageUrl={productImageUrl}
      productSeoUrl={productSeoUrl}
      productName={productName}
      hasReviews={hasReviews}
      averageRating={averageRating}
      totalReviews={totalReviews}
      isLoading={isLoading}
      isAdded={isAdded}
      handleAddToCart={handleAddToCart}
    />
  );
};
